import { z } from 'zod'

export const TaskTypeSchema = z.enum(['task', 'bug'])
export type TaskType = z.infer<typeof TaskTypeSchema>

export const BugDetailsSchema = z.object({
  stepsToReproduce: z.string(),
})
export type BugDetails = z.infer<typeof BugDetailsSchema>

// Напоминание задачи ровно одной природы: leadSeconds — за сколько до момента
// срока пнуть (0 = «в момент срока»), remindAt — календарный момент, срока
// задаче не требующий. Заполнено ровно одно поле — по нему и читается природа.
export const ReminderSchema = z.object({
  id: z.string(),
  leadSeconds: z.number().int().min(0).nullable(),
  remindAt: z.string().nullable(),
  createdAt: z.string(),
})
export type Reminder = z.infer<typeof ReminderSchema>

// Форма задачи на фронте (после snake→camel). Пустые строки бэка (parentId,
// assigneeId) и omitempty-указатели → null. Роллап/связи/gantt-флаги —
// не в TaskView, приходят отдельными ручками.
export const TaskSchema = z.object({
  id: z.string(),
  boardId: z.string(),
  columnId: z.string(),
  parentId: z.string().nullable(),
  title: z.string(),
  description: z.string(),
  type: TaskTypeSchema,
  assigneeId: z.string().nullable(),
  // Кто и когда назначил текущего исполнителя. Пусто у задач, назначенных до
  // появления полей, и у задач без исполнителя.
  assignedBy: z.string().nullable(),
  assignedAt: z.string().nullable(),
  // Календарный день срока `YYYY-MM-DD`: ни времени, ни зоны у него нет — момент
  // из него получается только вместе с зоной организации (shared/lib dueMoment).
  dueDate: z.string().nullable(),
  // Время суток срока (`HH:MM` в зоне организации); null — задача на весь день.
  // Зона тут именно организации, а не устройства: срок — обязательство студии,
  // «до 8 августа 17:00» одинаково для всех, кто на него смотрит.
  // Времени без дня не существует: снятие dueDate уносит и его.
  dueTime: z.string().nullable(),
  // Собственные напоминания задачи. Приходят только в карточке — списки доски за
  // ними не ходят; пустой набор = работает лестница студии.
  reminders: z.array(ReminderSchema).optional(),
  // Важность 1..10 — отдельно от complexity (та про трудозатраты).
  priority: z.number().int().min(1).max(10).nullable(),
  complexity: z.number().int().nullable(),
  estimateHours: z.number().nullable(),
  actualComplexity: z.number().int().nullable(),
  bugDetails: BugDetailsSchema.nullable(),
  // Порядок карточки внутри колонки (сортировка по position). Присутствует в
  // list-ответах; consumers гардят `?? 0`.
  position: z.number().int().optional(),
  // Блокер: задача ждёт незакрытую живую задачу. Только в list-ответах доски/
  // сотрудника; detail поля не несёт; consumers гардят `?? false`.
  blocked: z.boolean().optional(),
  // Обратная сторона: задача сама не закрыта и её ждут другие. Только в list-
  // ответах доски/сотрудника; detail поля не несёт; consumers гардят `?? false`.
  blocking: z.boolean().optional(),
  // Кто ведёт задачу прямо сейчас (открытый интервал). По нему кнопка отличает
  // «веду я» от «ведёт коллега». Приходит в читалках (карточка и списки), ответы
  // мутаций его не несут; consumers гардят `?? null`.
  activeEmployeeId: z.string().nullable().optional(),
  // Пометка «видно заказчику» (00-architecture § 4): открыть задачу клиенту
  // компании может тот сотрудник, кто вправе её править. Приходит в читалках;
  // отсутствие поля читается как false — безопасный дефолт, пока источник его не
  // выкатил. `_by`/`_at` — аудит «кто и когда это открыл».
  customerVisible: z.boolean().optional(),
  customerVisibleBy: z.string().nullable().optional(),
  customerVisibleAt: z.string().nullable().optional(),
  // Компания и проект задачи (выводятся через борд): по ним считается, можно ли
  // помечать вообще. Задача вне проекта/компании — нельзя, иначе флаг значил бы
  // «видно любому заказчику». Пусто — привязки нет либо её не отдали.
  companyId: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  archived: z.boolean(),
  archivedAt: z.string().nullable(),
  // Автор задачи (uuid сотрудника). У задач до введения поля пусто (omitempty на бэке).
  createdBy: z.string().nullable(),
  // Имя заказчика, заведшего задачу со своей витрины (createdBy тогда пуст).
  createdByCustomer: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type Task = z.infer<typeof TaskSchema>
